import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import PhiegLoader from "components/PhiegLoader";

/**
 * Gates page content behind the Phieg "O" loader. While `loading`, the ring
 * spins where the content will appear; when loading finishes, the ring fills
 * to a complete "O" and "Phieg" types in underneath, THEN the children fade
 * in.
 *
 * Deliberately quick: the in-page seal is the fast version of the boot
 * ceremony (~0.5s, vs ~1.2s on the BootSplash) so it reads as a flourish,
 * not a wait. Loads that resolve almost instantly (cache hits) skip the
 * flourish entirely and just fade the content in — a branded animation you
 * see on every click stops being branding. Mounting already-loaded shows the
 * children straight away (no fake splash).
 */
const SKIP_MS = 250; // loads quicker than this reveal with no flourish
const FILL_MS = 220; // ring draw-on
const TYPE_MS = 45; // per typed letter of "Phieg"
const HOLD_MS = 90; // beat after the word completes, before the reveal
const SEAL_MS = FILL_MS + TYPE_MS * 5 + HOLD_MS;

export default function LoadingGate({
  loading,
  label,
  minHeight = 260,
  fullscreen = false,
  children,
}) {
  const [phase, setPhase] = useState(loading ? "loading" : "done");
  const timer = useRef(null);
  const startedAt = useRef(loading ? Date.now() : null);

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (loading) {
      if (startedAt.current == null) startedAt.current = Date.now();
      setPhase("loading");
    } else {
      const took =
        startedAt.current == null ? 0 : Date.now() - startedAt.current;
      startedAt.current = null;
      setPhase((p) => {
        if (p === "done") return "done";
        if (took < SKIP_MS) return "done";
        // play the completion flourish, then reveal
        timer.current = setTimeout(() => setPhase("done"), SEAL_MS);
        return "sealing";
      });
    }
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [loading]);

  if (phase === "done") {
    return (
      <Box
        sx={{
          animation: "phiegGateIn 320ms ease both",
          "@keyframes phiegGateIn": {
            from: { opacity: 0, transform: "translateY(4px)" },
            to: { opacity: 1, transform: "none" },
          },
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(fullscreen
          ? {
              position: "fixed",
              inset: 0,
              zIndex: 1400,
              backgroundColor: "rgba(26, 42, 39, 0.65)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
            }
          : { minHeight, py: 6 }),
      }}
    >
      <PhiegLoader
        size={84}
        value={phase === "sealing" ? 100 : undefined}
        wordmark={phase === "sealing"}
        label={phase === "loading" ? label : undefined}
        fillMs={FILL_MS}
        typeMs={TYPE_MS}
      />
    </Box>
  );
}
